import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Drawer,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Chip,
    Tooltip,
    Autocomplete,
} from "@mui/material";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import CommonDatePicker from "../../../../Components/Common Components/CommonDatePicker";
import { DateTime } from "luxon";
import ToastMsg from "../../../../Components/ToastMsg/ToastMsg";
import { withTranslation } from "react-i18next";
import { CommonGridToolBarWithFilterText, CommonPatientDetails } from "../../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../../Utility/ImagePaths";
import { AmountsCard } from "../../../../Components/CommonCards/CommonCards";
import { localGetItem } from "../../../../Utility/Services/CacheProviderService";
import { set } from "date-fns";

const TelemedineReport = (props) => {
    const { t } = props;
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isErrorMsg, setIsErrorMsg] = useState(false);
    const [isErrorMsgText, setIsErrorMsgText] = useState("");
    const [fromDate, setFromDate] = useState(
        DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd")
    );
    const [toDate, setToDate] = useState(
        DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd")
    );
    const [dateIndex, setDateIndex] = useState(1);
    const [patientName, setPatientName] = useState("");
    const [uhid, setUhid] = useState("");
    const [mobile, setMobile] = useState("");
    const [totalDuration, setTotalDuration] = useState(0);
    const [loading, setLoading] = useState(false);
    const [FilterOpen, setFilterOpen] = useState(false);
    const [FilteredFromDate, setFilteredFromDate] = useState(
        DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd")
    );
    const [FilteredToDate, setFilteredToDate] = useState(
        DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd")
    );
    const [doctorNameList, setdoctorNameList] = useState([]);
    const [doctorName, setdoctorName] = useState(null);

    const errorMessage = (msg) => {
        setIsErrorMsgText(msg);
        setIsErrorMsg(true);
    };

    const msgClose = () => {
        setIsErrorMsg(false);
        setIsErrorMsgText("");
    };

    const getTeleReport = (fDate, tDate, pName, pid, mob, dName) => {
        try {
            setLoading(true);
            RestAPIService.getAll(`${Serviceurls.TELE_REPORT}?from_date=${fDate}&to_date=${tDate}&patient_name=${pName?.trim() || ""}&uhid=${pid?.trim() || ""}&mobile=${mob?.trim() || ""}&doctor_id=${dName?.doctor_id || ''}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        const dataWithSno = response.data.data.map((item, index) => ({
                            ...item,
                            sno: index + 1,
                        }));
                        setReportData(dataWithSno);
                        setTotalDuration(
                            response.data.total_minutes || 0
                        );
                        setFilteredFromDate(fromDate);
                        setFilteredToDate(toDate);
                        setLoading(false)
                    } else {
                        errorMessage(response.data.message);
                        setLoading(false)
                    }
                })
                .catch((error) => {
                    errorMessage(error.response?.data?.message || error.message);
                    setLoading(false)
                })
        } catch (e) {
            setLoading(false);
            errorMessage(e.message);
        }
    };

    useEffect(() => {
        getTeleReport(fromDate, toDate, patientName, uhid, mobile, doctorName);
        getDoctorNameList()
    }, []);

    const getDoctorNameList = () => {
        try {
            RestAPIService.getAll(Serviceurls.FO_CONFIG_OPDOCTORNAME)
                .then((response) => {
                    if (response.data.status === "success") {
                        setdoctorNameList(response.data.data || [])
                    }
                }).catch((error) => {
                    if (error?.response?.data?.status === 'fail') {
                        errorMessage(error.response.data.message)
                    } else {
                        errorMessage(error.message)
                    }
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }

    const handleSelectedDates = (from, to, index) => {
        if (DateTime.fromISO(to) < DateTime.fromISO(from)) {
            errorMessage("End date cannot be before start date");
            return;
        }
        setFromDate(from);
        setToDate(to);
        setDateIndex(index);
    };

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        if (name === "patientName") {
            const filteredValue = value.replace(/[^A-Za-z\s]/g, '');
            setPatientName(filteredValue);
        }
        if (name === "uhid") {
            const filteredValue = value.replace(/[^A-Za-z0-9]/g, '');
            setUhid(filteredValue);
        }
        if (name === "mobile") {
            const filteredValue = value.replace(/[^0-9]/g, '');
            setMobile(filteredValue);
        }
    };

    const clearFilters = () => {
        setFromDate(DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"));
        setToDate(DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"));
        setDateIndex(1);
        setPatientName("");
        setUhid("");
        setMobile("");
        setFilterOpen(false);
        setdoctorName(null);
        
        getTeleReport(
            DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"),
            DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"),
            "",
            "",
            "",
            null
        );
    };

    
    const applyFilters = () => {
        getTeleReport(fromDate, toDate, patientName, uhid, mobile, doctorName);
        setFilterOpen(false);
    };

    const closeFilter = () => {
        setFilterOpen(false);
    };

    const renderFilterScreen = () => {
        return (
            <Box className="eMed_rts_filter_container">
                <Box className="eMed_filter_Top">
                    <Typography variant="h6" fontWeight={600}>
                        {t("Filter")}
                    </Typography>
                    <Button onClick={closeFilter} className="eMed_usrconf_btn">
                        <img
                            src={ImagePaths.LabCloseButton.default}
                            alt="close"
                            className="eMed_action_img_small"
                        />
                    </Button>
                </Box>
                <Box className="eMed_rts_middle">
                    <Box className="eMed_date_filter">
                        <CommonDatePicker
                            FromDate={fromDate}
                            ToDate={toDate}
                            DateIndex={dateIndex}
                            SelectedDates={handleSelectedDates}
                            LabelText={t("Select Date")}
                            HideAllMenu={true}
                        />
                    </Box>
                    <TextField
                        name="patientName"
                        inputProps={{
                            maxLength: 20, emed_tid: 'patient_name',
                            pattern: "[A-Za-z\\s]*",
                            onInput: (e) => {
                                e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                            }
                        }}
                        label="Patient Name"
                        value={patientName}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                    <TextField
                        name="uhid"
                        label="UHID"
                        value={uhid}
                        inputProps={{
                            maxLength: 20, emed_tid: 'UHID',
                            pattern: "[A-Za-z0-9]*",
                            onInput: (e) => {
                                e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                            }
                        }}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                    <TextField
                        name="mobile"
                        label="Mobile"
                        value={mobile}
                        onChange={handleSearchChange}
                        inputProps={{
                            maxLength: 10,
                            pattern: "[0-9]*",
                            onInput: (e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                            }
                        }}
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                    <Autocomplete
                        size='small'
                        options={doctorNameList || []}
                        getOptionLabel={(option) => option?.doctor_name || ''}
                        value={doctorName}
                        onChange={(event, newValue) => {
                            setdoctorName(newValue);
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option?.doctor_id}>
                                {option?.doctor_name}
                            </li>
                        )}
                        sx={{ mt: 2 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('selectDoctor')}
                            />
                        )}
                        isOptionEqualToValue={(option, value) =>
                            option?.doctor_id === value?.doctor_id
                        }
                    />
                </Box>
                <Stack
                    spacing={2}
                    direction="row"
                    className="eMed_rts_btn"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Button variant="outlined" size="small" onClick={clearFilters}>
                        {t("Clear")}
                    </Button>
                    <Button variant="contained" size="small" onClick={applyFilters}>
                        {t("Search")}
                    </Button>
                </Stack>
            </Box>
        );
    };

    const gridToolBar = () => {
        const TextArray = [
            {
                label: "From Date",
                value: FilteredFromDate
                    ? DateTime.fromISO(FilteredFromDate).toFormat("dd-MM-yyyy")
                    : "",
            },
            {
                label: "To Date",
                value: FilteredToDate
                    ? DateTime.fromISO(FilteredToDate).toFormat("dd-MM-yyyy")
                    : "",
            },
        ];
        return (
            <CommonGridToolBarWithFilterText
                FilterTextArray={TextArray}
            />
        );
    };

    const exportToPDF = () => {
        const params = new URLSearchParams({
            from_date: fromDate,
            to_date: toDate,
            patient_name: patientName || "",
            uhid: uhid || "",
            mobile: mobile || "",
            doctor_id: doctorName?.doctor_id || '',
            export_type: "pdf",
        });
        RestAPIService.GetWithoutReplica(`${Serviceurls.TELE_REPORT}?${params}`)
            .then((response) => {
                const file = new Blob([response.data], { type: "application/pdf" });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            })
            .catch((error) =>
                errorMessage(error.response?.data?.message)
            );
    };

    const columns = [
        { field: "sno", headerName: t("S.No"), sortable: false, flex: 0.1 },
        {
            field: "patient_name",
            headerName: t("Patient Details"),
            flex: 0.2,
            renderCell: ({ row }) => (
                <CommonPatientDetails data={row} disablePhoto={true} />
            )
        },
        {
            field: "consultation_name",
            headerName: t("Consultation Name"),
            flex: 0.3,
            renderCell: (params) => (
                <div>{params.row?.consultation_name || '-'}</div>
            ),
        },
        {
            field: "date_time",
            headerName: t("Date & Time"),
            flex: 0.2,
            headerAlign: "center", align: "center",
            renderCell: (params) => (
                <div>{params.row?.date_time || "-"}</div>
            ),
        },
        {
            field: "call_duration",
            headerName: t("Call Duration (Min)"),
            flex: 0.2,
            renderCell: (params) => (
                <div>{params.row?.call_duration || "-"}</div>
            ),
        },
    ];


    return (
        <div className="eMed_rts_container reports_container">
            <Box component={"div"} className='eMed_Top_Container'>
                <Box component={"div"} className='eMed_Cards_Container'>
                    {AmountsCard(t("Total Minutes"), totalDuration.toString(), true)}
                </Box>
                <Box component={"div"} className='eMed_Action_Container'>
                    <Box component={"div"} className='eMed_Filter_Icon_div'>
                        <Button className='eMed_usrconf_btn' sx={{ ml: "1vw" }} emed_tid={"eMed_action_img_TestID"} onClick={() => { setFilterOpen(true) }}>
                            <img src={ImagePaths.Filter.default} alt="filter" className='eMed_action_img' />
                        </Button>
                    </Box>
                    <Tooltip title={t("Print")} placement='top' arrow>
                        <Button className='eMed_usrconf_btn' emed_tid={"eMed_usrconf_btn_TestID01"} onClick={exportToPDF}>
                            <img src={ImagePaths.LabPrintIcon.default} alt="print" className='eMed_action_img' />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
            <div className="eMed_rts_table reports_table_card">
                <DataGrid
                    rows={reportData}
                    columns={columns}
                    getRowId={(row) => row.sno}
                    page={page}
                    onPageChange={setPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    rowsPerPageOptions={[10, 20, 30]}
                    pagination
                    sortingMode="client"
                    disableSelectionOnClick
                    loading={loading}
                    localeText={{
                        toolbarColumns: "",
                        toolbarDensity: "",
                        toolbarExport: "",
                        toolbarFilters: "",
                        toolbarExportPrint: ""
                    }}
                    components={{
                        Toolbar: gridToolBar,
                        NoRowsOverlay: () => (
                            <Stack height="100%" alignItems="center" justifyContent="center">
                                {t("No Data found")}
                            </Stack>
                        ),
                    }}
                    headerHeight={40}
                />
            </div>
            <Drawer
                anchor="right"
                open={FilterOpen}
                ModalProps={{ onBackdropClick: closeFilter }}
            >
                {renderFilterScreen()}
            </Drawer>
            {isErrorMsg && (
                <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />
            )}
        </div>
    );
};

export default withTranslation()(TelemedineReport);


